import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Button,
  MobileStepper,
  CircularProgress,
} from '@mui/material'
import { theme } from 'common/theme'
import useCurrentTeacher from 'common/hooks/useCurrentTeacher'
import api from 'common/services/api'
import TermsOfServiceComponent from './termsOfService'
import ErrorMessage from './ErrorMessage'

const MAX_STEPS = 4

function AwakeningStepperComponent() {
  const { currentTeacher } = useCurrentTeacher()
  const [activeStep, setActiveStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const reset = () => {
    setActiveStep(0)
    setLoading(false)
    setError(null)
  }

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        event.origin !== window.location.origin ||
        event.data?.type !== 'resetStepperModal'
      ) {
        return
      }
      reset()
    }

    reset() // initial reset
    window.parent.addEventListener('message', handleMessage)
    return () => {
      window.parent.removeEventListener('message', handleMessage)
    }
  }, [])

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1)
  }

  const handleOkPressed = () => {
    if (!currentTeacher) return
    setLoading(true)
    api({
      url: '/api2/teacher/enable-awakening',
      method: 'POST',
      data: {
        enableAwakeningForNewStudents: true,
        tCode: currentTeacher.code,
        tSecret: currentTeacher.getSecret(),
      },
    })
      .then(() => {
        window.parent.postMessage(
          { type: 'awakeningStepperAction', status: 'success' },
          window.location.origin
        )
        window.parent.location.href = '/#/teacher/awakening'
      })
      .catch((error) => {
        setError(
          `${error?.response?.data?.message}: ${error?.response?.data?.reason}`
        )
      })
  }

  const isInAwakeningPage = window.parent.location.href.includes('/awakening')

  if (error) {
    return <ErrorMessage error={error} onRetry={reset} />
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '600px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {activeStep === 0 && (
        <>
          <Box
            component='img'
            src={`${process.env.REACT_APP_IFRAME_IMAGES_URL}images/teacher/awakening/step1.png`}
            width='700px'
          />
          <Box textAlign='center' sx={{ p: 2 }}>
            <Typography variant='h5' sx={{ mb: 1 }}>
              XtraMath introduces Awakening
            </Typography>
            <Typography variant='body2'>
              We&apos;re excited to offer free access to Awakening, the
              award-winning, game-based learning experience. After students
              finish their daily XtraMath practice they can customize an avatar
              and explore a 3D world with curriculum-aligned games that
              reinforce math skills in a fun way.
            </Typography>
          </Box>
        </>
      )}

      {activeStep === 1 && (
        <>
          <Box
            component='img'
            src={`${process.env.REACT_APP_IFRAME_IMAGES_URL}images/teacher/awakening/step2.png`}
            width='700px'
          />
          <Box textAlign='center' sx={{ p: 2 }}>
            <Typography variant='h5' sx={{ mb: 1 }}>
              Boost Math Fluency Through Play
            </Typography>
            <Typography variant='body2'>
              Awakening is an optional bonus intended to enhance learning time,
              boost motivation, build student confidence, and improve math
              fluency. Games are created in partnership with professional game
              studios and aligned with STEM standards.
            </Typography>
          </Box>
        </>
      )}

      {activeStep === 2 && (
        <>
          <Box
            component='img'
            src={`${process.env.REACT_APP_IFRAME_IMAGES_URL}images/teacher/awakening/step3.png`}
            width='700px'
          />
          <Box textAlign='center' sx={{ p: 2 }}>
            <Typography variant='h5' sx={{ mb: 1 }}>
              Designed for Control and Flexibility
            </Typography>
            <Typography variant='body2'>
              It&apos;s simple - no new accounts to manage and no personal data
              is shared. Educators can easily enable or disable Awakening for a
              specific student or an entire class right from XtraMath.
            </Typography>
          </Box>
        </>
      )}

      {activeStep === 3 && (
        <>
          <Box
            component='img'
            src={`${process.env.REACT_APP_IFRAME_IMAGES_URL}images/teacher/awakening/step4.png`}
          />
          <Box textAlign='center' sx={{ p: 2 }}>
            <Typography variant='h6' sx={{ mb: 1 }}>
              No Cost. No Ads. Just Bonus Learning.
            </Typography>
            <Typography variant='body2'>
              To activate game-based learning for your students, click OK after
              reviewing the Awakening Terms and Conditions. You can opt-out at
              any time.
            </Typography>
            <Box
              sx={{
                mt: 3,
                backgroundColor: '#F4F4F4',
                borderRadius: 0,
                p: 1,
                textAlign: 'left',
                height: '150px',
                overflow: 'auto',
                fontSize: 14,
              }}
            >
              <TermsOfServiceComponent />
            </Box>
          </Box>
        </>
      )}

      <Box sx={{ display: 'flex', alignItems: 'end', flex: 1 }}>
        <MobileStepper
          variant='dots'
          steps={MAX_STEPS}
          position='static'
          activeStep={activeStep}
          sx={{
            flexGrow: 1,
            justifyContent: 'center',
            '& .MuiMobileStepper-dotActive': {
              backgroundColor: theme.palette.secondary.main,
            },
          }}
          nextButton={
            <>
              <Box sx={{ flex: 1 }} />
              {activeStep < MAX_STEPS - 1 ? (
                <Button
                  variant='contained'
                  color='secondary'
                  onClick={handleNext}
                >
                  Next
                </Button>
              ) : (
                <Box sx={{ position: 'relative' }}>
                  {isInAwakeningPage ? (
                    <Button
                      variant='text'
                      sx={{
                        color: 'common.black',
                        position: 'absolute',
                        top: 0,
                        right: 80,
                        width: 100,
                      }}
                      onClick={() => {
                        const closeButton =
                          window.parent.document.querySelector(
                            '#awakeningPopup .closeIcon'
                          ) as HTMLElement
                        closeButton.click()
                      }}
                    >
                      MORE INFO
                    </Button>
                  ) : (
                    <Button
                      component='a'
                      href='/#/teacher/awakening'
                      target='parent'
                      variant='text'
                      sx={{
                        color: 'common.black',
                        position: 'absolute',
                        top: 0,
                        right: 80,
                        width: 100,
                      }}
                    >
                      MORE INFO
                    </Button>
                  )}
                  {loading ? (
                    <CircularProgress sx={{ ml: 1 }} size='1.25rem' />
                  ) : (
                    <Button
                      variant='contained'
                      color='secondary'
                      onClick={handleOkPressed}
                    >
                      OK
                    </Button>
                  )}
                </Box>
              )}
            </>
          }
          backButton={
            <>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{ mr: 1 }}
              >
                Back
              </Button>
              <Box sx={{ flex: 1 }} />
            </>
          }
        />
      </Box>
    </Box>
  )
}

export default AwakeningStepperComponent
